import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { useQuery } from "@tanstack/react-query";
import { GetAllDetailReport } from "../../../lib/api";
import {
  ShowLoadingAnimation,
  ShowNoDataAnimation,
} from "../../../components/common/Animation";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import Flatpickr from "react-flatpickr";
import { useState } from "react";
import { toast } from "react-toastify";

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const DetailReport = () => {
  const [dateRange, setDateRange] = useState([new Date(), new Date()]);
  const [searchParam, setSearchParam] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [isDownloadExcel, setIsDownloadExcel] = useState(false);
  const pageSize = 10;
  const startDate = dateRange[0] ? formatDate(dateRange[0]) : null;
  const endDate = dateRange[1] ? formatDate(dateRange[1]) : null;
  const { isLoading, isError, data } = useQuery({
    queryKey: [
      "GetAllDetailReport",
      startDate,
      endDate,
      pageNumber,
      searchParam,
    ],
    queryFn: () =>
      GetAllDetailReport(startDate, endDate, pageNumber, pageSize, searchParam),
    keepPreviousData: true,
  });

  const isUserData = data?.data?.tokenDetails?.tokenDetails?.data;

  const pageCount = data?.data?.tokenDetails?.tokenDetails?.totalPages;

  const handlePageChange = (_, newPage) => {
    setPageNumber(newPage);
  };

  const handleDownloadExcel = async () => {
    if (isLoading) return;
    if (data?.data?.tokenDetails?.tokenDetails?.totalCount === 0) {
      toast.error("No data to Download", {
        position: toast.POSITION.TOP_RIGHT,
      });
    } else {
      setIsDownloadExcel(true);
      const APIResponse = data?.data?.tokenDetails?.tokenDetails;
      const totalPages = APIResponse?.pageSize * APIResponse?.totalPages;
      const dataResponse = await GetAllDetailReport(
        startDate,
        endDate,
        1,
        totalPages,
        searchParam
      );
      if (dataResponse.status === 200) {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Token Detail Report");
        worksheet.columns = [
          { header: "Date", key: "date", width: 15 },
          { header: "Token Number", key: "tokenNumber", width: 20 },
          { header: "Customer Name", key: "customerName", width: 20 },
          { header: "Mobile Number", key: "mobileNumber", width: 20 },
          {
            header: "Token Generated",
            key: "tokenGeneratedTime",
            width: 20,
          },
          {
            header: "Department From",
            key: "departmentFromName",
            width: 20,
          },
          { header: "Department To", key: "departmentToName", width: 20 },
          { header: "CounterName", key: "counterName", width: 15 },
          { header: "Called Time", key: "calledTime", width: 15 },
          { header: "Status", key: "status", width: 15 },
        ];
        const headerRow = worksheet.getRow(1);
        headerRow.eachCell((cell) => {
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF1F2937" },
          };
          cell.alignment = { vertical: "middle", horizontal: "center" };
        });
        dataResponse?.data?.tokenDetails?.tokenDetails?.data?.forEach(
          (item: any) => {
            worksheet.addRow({
              date: item.CreatedDate?.split("T")[0],
              tokenNumber: item.DepFromPrefix + item.TokenValue,
              customerName: item.CustomerName,
              mobileNumber: item.CustomerMobileNumber,
              tokenGeneratedTime: item.StatusCreatedTime,
              departmentFromName: item.DepFromName,
              departmentToName: item.DepToName,
              counterName: item.CounterName,
              calledTime: item.OnCallTime,
              status: item.CurrentTokenStatus,
            });
          }
        );
        const buffer = await workbook.xlsx.writeBuffer();
        const date = new Date().toISOString().split("T")[0];
        saveAs(new Blob([buffer]), `Token_Detail_Report-${date}.xlsx`);
      } else {
        alert("No Data Available to Download");
      }
      setIsDownloadExcel(false);
    }
  };

  return (
    <div className="py-5">
      <div className="flex justify-between items-center mb-2">
        <h4 className="text-xl min-[480px]:text-2xl sm:text-3xl font-bold">
          Token Detail Report
        </h4>
        <div className="relative">
          <img
            className="cursor-pointer flex  w-[60px]"
            onClick={handleDownloadExcel}
            src="/image/element/excel.png"
            alt="excel_icon"
          />
          {isDownloadExcel && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded z-10">
              <div className="flex justify-center items-center m-auto">
                <div role="status">
                  <svg
                    aria-hidden="true"
                    className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="currentColor"
                    />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentFill"
                    />
                  </svg>
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-col min-[480px]:flex-row justify-between items-center gap-y-2">
        {/* Date Range Picker */}
        <div
          id="date-range-picker"
          className="flex items-center w-full min-[480px]:w-auto"
        >
          <div className="relative w-full">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <svg
                className="w-4 h-4 text-gray-500 dark:text-gray-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M20 4a2 2 0 0 0-2-2h-2V1a1 1 0 0 0-2 0v1h-3V1a1 1 0 0 0-2 0v1H6V1a1 1 0 0 0-2 0v1H2a2 2 0 0 0-2 2v2h20V4ZM0 18a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8H0v10Zm5-8h10a1 1 0 0 1 0 2H5a1 1 0 0 1 0-2Z" />
              </svg>
            </div>
            <Flatpickr
              value={dateRange}
              onChange={(dates) => setDateRange(dates)}
              options={{ mode: "range", dateFormat: "Y-m-d" }}
              className="bg-gray-50 border border-gray-300 w-full md:w-auto h-full text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 pl-10 p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              placeholder="Select date range"
              inputp
            />
          </div>
        </div>

        {/* Token Number Search */}
        <div className="m-0 w-full min-[480px]:w-auto">
          <input
            className="border border-gray-400 rounded-[5px] px-1 py-1.5 focus:outline-none w-full min-[480px]:w-auto"
            placeholder="Token Number"
            onChange={(e) => setSearchParam(e.target.value)}
          />
          {/*  <TextField
            id="outlined-basic"
            label="Token Number"
            variant="outlined"
            size="small"
            onChange={(e) => setSearchParam(e.target.value)}
          /> */}
        </div>
      </div>

      <div className="w-full">
        <div className="mt-5 border-2 rounded-lg">
          <div className="overflow-auto">
            <TableContainer>
              <Table>
                <TableHead className="bg-gray-800 rounded-xl">
                  <TableRow>
                    <TableCell style={{ color: "white" }}>Date</TableCell>
                    <TableCell style={{ color: "white" }}>
                      Token Number
                    </TableCell>
                    <TableCell style={{ color: "white" }}>
                      Customer Name
                    </TableCell>
                    <TableCell style={{ color: "white", textAlign: "center" }}>
                      Mobile Number
                    </TableCell>
                    <TableCell style={{ color: "white", textAlign: "center" }}>
                      Token Generated Time
                    </TableCell>
                    <TableCell style={{ color: "white", textAlign: "center" }}>
                      Department From Name
                    </TableCell>
                    <TableCell style={{ color: "white", textAlign: "center" }}>
                      Department To Name
                    </TableCell>
                    <TableCell style={{ color: "white", textAlign: "center" }}>
                      Counter Name
                    </TableCell>
                    <TableCell style={{ color: "white", textAlign: "center" }}>
                      Called Time
                    </TableCell>
                    <TableCell style={{ color: "white", textAlign: "center" }}>
                      Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <div className="w-44 flex justify-center m-auto">
                          <ShowLoadingAnimation />
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : isUserData && isUserData.length > 0 ? (
                    isUserData.map((user, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {user.CreatedDate
                            ? new Date(user.CreatedDate)
                                .toISOString()
                                .split("T")[0]
                            : "N/A"}
                        </TableCell>
                        <TableCell>
                          {user.DepFromPrefix}
                          {user.TokenValue}
                        </TableCell>
                        <TableCell>{user.CustomerName}</TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          {user.CustomerMobileNumber}
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          {user.StatusCreatedTime}
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          {user.DepFromName}
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          {user.DepToName}
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          {user.CounterName}
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          {user.OnCallTime}
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          {user.CurrentTokenStatus}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        No Data Available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </div>
      </div>

      <div className="flex justify-center">
        {isError && !isUserData && (
          <div className="w-44 flex justify-center">
            <ShowNoDataAnimation />
          </div>
        )}
      </div>

      {!isLoading && isUserData && isUserData.length > 0 && (
        <div className="flex justify-center items-center w-full my-4">
          <Stack spacing={2}>
            <Pagination
              count={pageCount}
              page={pageNumber}
              onChange={handlePageChange}
              color="primary"
            />
          </Stack>
        </div>
      )}
    </div>
  );
};

export default DetailReport;
